import type {
    ClaimTemplateData,
    ComplaintTemplateData,
    LodgementTemplateData,
} from '../../types/business/communication';

// Prefix for product type custom fields in templates
export const PRODUCT_TYPE_CUSTOM_FIELD_TAG_PREFIX = 'product_type__';

// Prefix for policy custom fields in templates
export const POLICY_CUSTOM_FIELD_TAG_PREFIX = 'policy_custom__';

/**
 * Tags whose value is a URL. `processCommunicationTemplate` renders these as a clickable
 * anchor on the HTML (email body) path only; SMS bodies and subjects still receive the bare
 * URL because they are processed with `stripHtml` or through the plain-text `@tag` path.
 * Membership is explicit — no suffix heuristics — so a new link tag is a deliberate choice.
 */
export const COMM_TEMPLATE_LINK_TAGS = [
    'claim_link',
    'claim_console_link',
    'summary_link',
    'resume_link',
] as const satisfies (keyof LodgementTemplateData)[];

const CLAIM_TEMPLATE_TAGS = [
    //Main
    'claim_id',
    'claim_owner_full_name',
    'claim_status',
    'claim_reported_at_date',
    'claim_reported_at_datetime',
    'decision',
    'triage_outcome',
    'cause_of_loss',
    'date_of_loss',
    'address_line_1_of_loss',
    'address_line_2_of_loss',
    'suburb_of_loss',
    'state_of_loss',
    'postcode_of_loss',
    'full_address_of_loss',
    'product_type',
    'external_id',
    'handling_party_1',
    'handling_party_2',
    'handling_party_3',

    // Policy
    'insurer_company_name',
    'policy_name',
    'policy_number',
    'policy_term_from',
    'policy_term_to',
    'policy_issue_date',
    'policy_status',
    'policy_other_info',

    // Insurer
    'insurer_first_name',
    'insurer_last_name',
    'insurer_full_name',
    'insurer_email',
    'insurer_phone',

    //Policy holder
    'policy_holder_company',
    'policy_holder_first_name',
    'policy_holder_last_name',
    'policy_holder_full_name',
    'policy_holder_email',
    'policy_holder_phone',
    'policy_holder_address_line_1',
    'policy_holder_address_line_2',
    'policy_holder_postcode',
    'policy_holder_state',
    'policy_holder_suburb',
    'policy_holder_full_address',
    'policy_holder_dob',
    'policy_holder_pay_id',
    'policy_holder_acc_name',
    'policy_holder_bsb',
    'policy_holder_acc_no',
    'policy_holder_itc',
    'policy_holder_abn',

    //Broker
    'broker_company',
    'broker_first_name',
    'broker_last_name',
    'broker_full_name',
    'broker_email',
    'broker_phone',
    'broker_branch',

    //Third party
    'third_party_first_name',
    'third_party_last_name',
    'third_party_full_name',
    'third_party_email',
    'third_party_phone',
    'third_party_type',

    //Driver
    'driver_first_name',
    'driver_last_name',
    'driver_full_name',
    'driver_email',
    'driver_phone',

    //Self-serve
    'claim_link',

    //Financials
    'total_reserve',
    'all_reserves',
    'total_excess',
    'all_excesses',
] satisfies (keyof ClaimTemplateData)[];

/** The submission-side merge fields a lodgement template can use on top of the claim vocabulary. */
export const LODGEMENT_TEMPLATE_TAGS = [
    'submission_reference',
    'lodgement_link_name',
    'summary_link',
    'resume_link',
    'claim_console_link',
    'product_name',
    'version_name',
    'org_name',
    'recipient_first_name',
    'recipient_full_name',
] as const satisfies (keyof LodgementTemplateData)[];

/** Which lodgement email a template renders (mirrors the CommTemplatePurpose enum). */
export const LODGEMENT_TEMPLATE_PURPOSES = ['CLAIMANT_CONFIRMATION', 'INTERNAL_NOTIFICATION', 'DRAFT_RESUME'] as const;
export type LodgementTemplatePurpose = (typeof LODGEMENT_TEMPLATE_PURPOSES)[number];

export const LODGEMENT_TEMPLATE_PURPOSE_LABELS = {
    CLAIMANT_CONFIRMATION: 'Claimant confirmation',
    INTERNAL_NOTIFICATION: 'Internal notification',
    DRAFT_RESUME: 'Draft resume',
} as const satisfies Record<LodgementTemplatePurpose, string>;

/** Internal names of the system-console defaults seeded for each lodgement purpose and channel. */
export const LODGEMENT_SYSTEM_TEMPLATE_NAMES = {
    CLAIMANT_CONFIRMATION: {
        EMAIL: 'lodgement_claimant_confirmation_email',
        SMS: 'lodgement_claimant_confirmation_sms',
    },
    INTERNAL_NOTIFICATION: { EMAIL: 'lodgement_internal_notification_email', SMS: null },
    DRAFT_RESUME: { EMAIL: 'lodgement_draft_resume_email', SMS: null },
} as const satisfies Record<LodgementTemplatePurpose, { EMAIL: string; SMS: string | null }>;

export const COMM_TEMPLATE_TAGS = {
    claim: CLAIM_TEMPLATE_TAGS,
    lodgement: [...CLAIM_TEMPLATE_TAGS, ...LODGEMENT_TEMPLATE_TAGS] satisfies (keyof LodgementTemplateData)[],
    complaint: [
        'complaint_id',
        'complainant_first_name',
        'complainant_last_name',
        'complainant_full_name',
        'complaint_status',
        'complaint_category',
        'complaint_received_date',
        'complaint_link',
        'org_name',
    ] satisfies (keyof ComplaintTemplateData)[],
    incident: [],
    org: [],
    user: [],
    risk: [],
    task: [],
    control: [],
    mspr: [],
};

/**
 * Internal names of the CommTemplate records used to render the automated
 * acknowledgment sent to a complainant when a complaint is lodged. An org
 * configures one published template per channel; the acknowledgment is skipped
 * for a channel when no matching published template exists.
 */
export const COMPLAINT_ACKNOWLEDGMENT_TEMPLATE = {
    EMAIL: 'complaint_acknowledgment_email',
    SMS: 'complaint_acknowledgment_sms',
} as const;
